import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ChangePasswordModal from './modals/ChangePasswordModal';
import { useTheme } from '../contexts/ThemeContext';
import { useDarkMode } from '../contexts/DarkModeContext';
import { useNotifications } from '../contexts/NotificationContext';
import { resolveBrandLogo, handleBrandLogoError } from '../utils/brandLogo';
import buildInfo from '../buildInfo.json';

const formatBuildDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

// Orders shortcut destination per role. Matches the sidebar "Orders" entry.
const ORDERS_PATH_BY_ROLE = {
  restaurant: '/restaurant/orders/list',
  branch:     '/branch/orders/list',
  kitchen:    '/kitchen/orders/list',
  cashier:    '/cashier/operations/orders',
  captain:    '/captain/operations/orders',
};

const Header = ({ onToggleSidebar, sidebarCollapsed, sidebarVisible, onLogout }) => {
  const { primaryColor, logoUrl, feviconUrl } = useTheme();
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const { notifications, unreadCount, markAsRead, markAllAsRead, clearNotifications } = useNotifications();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [userRole, setUserRole] = useState('');
  const [shopName, setShopName] = useState('');
  const [userMobile, setUserMobile] = useState('');
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState('');

  const navigate = useNavigate();
  const notificationRef = useRef(null);
  const profileRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 992);
    };

    window.addEventListener('resize', handleResize);

    // Get user information from localStorage
    const role = localStorage.getItem('UserRole');
    const shop = localStorage.getItem('ShopName');
    const mobile = localStorage.getItem('UserMobile');
    const photo = localStorage.getItem('profilePhoto');

    if (role) setUserRole(role);
    if (shop) setShopName(shop);
    if (mobile) setUserMobile(mobile);
    if (photo) setProfilePhoto(photo);

    // Listen for profile photo updates
    const handlePhotoUpdate = () => {
      const updatedPhoto = localStorage.getItem('profilePhoto');
      setProfilePhoto(updatedPhoto || '');
    };
    window.addEventListener('profilePhotoUpdated', handlePhotoUpdate);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('profilePhotoUpdated', handlePhotoUpdate);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getToggleIcon = () => {
    if (isMobile) {
      return sidebarVisible ? 'bi-x-lg' : 'bi-list';
    }
    return sidebarCollapsed ? 'bi-list' : 'bi-chevron-left';
  };

  const toggleNotifications = () => {
    setShowNotifications(!showNotifications);
    setShowProfileMenu(false);
  };

  const toggleProfileMenu = () => {
    setShowProfileMenu(!showProfileMenu);
  };

  const handleNavigateToProfile = () => {
    setShowProfileMenu(false);
    navigate('/profile');
  };

  const handleAvatarClick = (e) => {
    e.stopPropagation();
    handleNavigateToProfile();
  };

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    }
  };

  const handleChangePassword = (e) => {
    e.preventDefault();
    setShowProfileMenu(false);
    setShowChangePasswordModal(true);
  };


  return (
    <header className={`dms-header ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <div className="header-menu" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <i
          className={`bi ${getToggleIcon()} fs-responsive-md`}
          style={{ cursor: 'pointer' }}
          onClick={onToggleSidebar}
        ></i>
        {/* Header logo — mobile only. On desktop the sidebar brand is authoritative. */}
        {isMobile && (
          <img
            src={resolveBrandLogo(logoUrl, feviconUrl)}
            alt="Logo"
            style={{ height: '34px', width: 'auto', maxWidth: '140px', objectFit: 'contain' }}
            onError={handleBrandLogoError(feviconUrl)}
          />
        )}
      </div>

      <div className="header-actions">

        {/* Orders shortcut — quick jump to the Orders list for this role */}
        {ORDERS_PATH_BY_ROLE[userRole] && (
          <div
            className="orders-shortcut"
            onClick={() => navigate(ORDERS_PATH_BY_ROLE[userRole])}
            title="Orders"
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: `${primaryColor}10`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              marginRight: '8px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = `${primaryColor}20`; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = `${primaryColor}10`; }}
          >
            <i className="bi bi-bag-check" style={{ fontSize: '18px', color: primaryColor }}></i>
          </div>
        )}

        {/* Notifications — visible for restaurant, kitchen, cashier */}
        {(userRole === 'restaurant' || userRole === 'kitchen' || userRole === 'cashier') && <div className="notification-container" ref={notificationRef}>
          <div
            className="notification-icon"
            onClick={toggleNotifications}
            style={{
              position: 'relative',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: `${primaryColor}10`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = `${primaryColor}20`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = `${primaryColor}10`;
            }}
          >
            <i className="bi bi-bell" style={{ fontSize: '18px', color: primaryColor }}></i>
            {unreadCount > 0 && (
              <span
                className="notification-badge"
                style={{
                  position: 'absolute',
                  top: '2px',
                  right: '2px',
                  minWidth: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  background: '#dc3545',
                  color: '#fff',
                  fontSize: '10px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid #fff'
                }}
              >
                {unreadCount}
              </span>
            )}
          </div>
          
          {showNotifications && (
            <div
              className="notification-dropdown"
              style={{
                position: 'absolute',
                top: '100%',
                right: '0',
                marginTop: '8px',
                background: '#fff',
                borderRadius: '12px',
                boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                border: `1px solid ${primaryColor}20`,
                minWidth: '280px',
                overflow: 'hidden',
                zIndex: 1000
              }}
            >
              <div
                className="dropdown-header"
                style={{
                  padding: '14px 16px',
                  background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <h6 style={{ margin: 0, color: '#fff', fontWeight: '600', fontSize: '15px' }}>Notifications</h6>
                {unreadCount > 0 && (
                  <span
                    onClick={(e) => { e.stopPropagation(); markAllAsRead(); }}
                    style={{
                      background: 'rgba(255,255,255,0.2)',
                      color: '#fff',
                      padding: '3px 10px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    {unreadCount} new - Mark all read
                  </span>
                )}
              </div>
              <div className="notification-list" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                {notifications.length === 0 ? (
                  <div style={{
                    padding: '30px 20px',
                    textAlign: 'center',
                    color: '#999'
                  }}>
                    <i className="bi bi-bell-slash" style={{ fontSize: '32px', display: 'block', marginBottom: '8px', color: '#ddd' }}></i>
                    <span style={{ fontSize: '13px' }}>No notifications</span>
                  </div>
                ) : (
                  notifications.map((notification) => (
                    <div
                      key={notification.id}
                      onClick={() => markAsRead(notification.id)}
                      style={{
                        padding: '12px 16px',
                        borderBottom: '1px solid #f0f0f0',
                        cursor: 'pointer',
                        transition: 'background 0.2s',
                        background: notification.unread ? `${primaryColor}08` : 'transparent'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {notification.unread && (
                          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: primaryColor, flexShrink: 0 }}></span>
                        )}
                        <div className="notification-title" style={{ fontWeight: notification.unread ? '600' : '500', fontSize: '13px', color: '#333' }}>{notification.title}</div>
                      </div>
                      <div className="notification-message" style={{ fontSize: '12px', color: '#666', marginTop: '2px', paddingLeft: notification.unread ? '14px' : '0' }}>{notification.message}</div>
                      <div className="notification-time" style={{ fontSize: '11px', color: primaryColor, marginTop: '4px', paddingLeft: notification.unread ? '14px' : '0' }}>{notification.time}</div>
                    </div>
                  ))
                )}
              </div>
              <div
                className="dropdown-footer"
                style={{
                  padding: '10px 16px',
                  borderTop: '1px solid #eee',
                  textAlign: 'center'
                }}
              >
                <button
                  type="button"
                  onClick={() => { clearNotifications(); setShowNotifications(false); }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#dc3545',
                    fontWeight: '600',
                    fontSize: '13px',
                    cursor: 'pointer',
                    padding: '6px 16px',
                    borderRadius: '6px',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#dc354510';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <i className="bi bi-trash3" style={{ marginRight: '5px' }}></i>
                  Clear all notifications
                </button>
              </div>
            </div>
          )}
        </div>}

        {/* Profile Menu */}
        <div className="profile-container" ref={profileRef}>
          <div
            className="user-profile"
            onClick={toggleProfileMenu}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '6px 12px',
              borderRadius: '25px',
              background: `linear-gradient(135deg, ${primaryColor}15 0%, ${primaryColor}08 100%)`,
              border: `1px solid ${primaryColor}30`,
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            <div
              className="user-avatar"
              onClick={handleAvatarClick}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: profilePhoto ? 'transparent' : `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}cc 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: '16px',
                fontWeight: '600',
                textTransform: 'uppercase',
                boxShadow: `0 2px 8px ${primaryColor}40`,
                overflow: 'hidden'
              }}
            >
              {profilePhoto ? (
                <img src={profilePhoto} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                  onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.textContent = (shopName || userRole || 'U').charAt(0); }} />
              ) : (
                (shopName || userRole || 'U').charAt(0)
              )}
            </div>
            <div className="user-info d-none d-md-block" onClick={handleAvatarClick}>
              <div className="user-name" style={{
                fontSize: '13px',
                fontWeight: '600',
                color: '#333',
                lineHeight: '1.2'
              }}>
                {shopName || userRole}
              </div>
              <div className="user-role" style={{
                fontSize: '11px',
                color: primaryColor,
                fontWeight: '500'
              }}>
                {userMobile}
              </div>
            </div>
            <i
              className="bi bi-chevron-down d-none d-md-block"
              style={{
                fontSize: '12px',
                color: primaryColor,
                transition: 'transform 0.3s ease',
                transform: showProfileMenu ? 'rotate(180deg)' : 'rotate(0deg)'
              }}
            ></i>
          </div>

          {showProfileMenu && (
            <div
              className="profile-dropdown"
              style={{
                position: 'absolute',
                top: '100%',
                right: '0',
                marginTop: '8px',
                background: isDarkMode ? 'rgba(15, 15, 25, 0.95)' : '#fff',
                borderRadius: '12px',
                boxShadow: isDarkMode ? '0 20px 60px rgba(0, 0, 0, 0.5)' : '0 10px 40px rgba(0,0,0,0.15)',
                border: isDarkMode ? `1px solid rgba(var(--theme-primary-rgb), 0.15)` : `1px solid ${primaryColor}20`,
                minWidth: '220px',
                overflow: 'hidden',
                zIndex: 1000,
                animation: 'slideDown 0.2s ease'
              }}
            >
              <div
                className="dropdown-header"
                style={{
                  padding: '16px',
                  background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}
              >
                <div
                  className="profile-avatar"
                  style={{
                    width: '45px',
                    height: '45px',
                    borderRadius: '50%',
                    background: profilePhoto ? 'transparent' : 'rgba(255,255,255,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: '20px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    overflow: 'hidden'
                  }}
                >
                  {profilePhoto ? (
                    <img src={profilePhoto} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                      onError={(e) => { e.target.style.display = 'none'; }} />
                  ) : (
                    (shopName || userRole || 'U').charAt(0)
                  )}
                </div>
                <div className="profile-details">
                  <div className="profile-name" style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#fff'
                  }}>
                    {shopName || userRole}
                  </div>
                  <div className="profile-email" style={{
                    fontSize: '12px',
                    color: 'rgba(255,255,255,0.85)'
                  }}>
                    {userMobile}
                  </div>
                </div>
              </div>
              <div className="profile-menu-list" style={{ padding: '8px' }}>
                <button
                  type="button"
                  className="profile-menu-item"
                  onClick={handleNavigateToProfile}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    width: '100%',
                    padding: '10px 12px',
                    border: 'none',
                    background: 'transparent',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontSize: '13px',
                    color: isDarkMode ? '#e2e8f0' : '#333'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = `${primaryColor}10`;
                    e.currentTarget.style.color = primaryColor;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = isDarkMode ? '#e2e8f0' : '#333';
                  }}
                >
                  <i className="bi bi-person-fill" style={{ fontSize: '16px' }}></i>
                  <span>My Profile</span>
                </button>
                <button
                  type="button"
                  className="profile-menu-item"
                  onClick={handleChangePassword}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    width: '100%',
                    padding: '10px 12px',
                    border: 'none',
                    background: 'transparent',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontSize: '13px',
                    color: isDarkMode ? '#e2e8f0' : '#333'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = `${primaryColor}10`;
                    e.currentTarget.style.color = primaryColor;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = isDarkMode ? '#e2e8f0' : '#333';
                  }}
                >
                  <i className="bi bi-lock-fill" style={{ fontSize: '16px' }}></i>
                  <span>Change Password</span>
                </button>
                <button
                  type="button"
                  className="profile-menu-item"
                  onClick={() => { toggleDarkMode(); setShowProfileMenu(false); }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    width: '100%',
                    padding: '10px 12px',
                    border: 'none',
                    background: 'transparent',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontSize: '13px',
                    color: isDarkMode ? '#e2e8f0' : '#333'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = `${primaryColor}10`;
                    e.currentTarget.style.color = primaryColor;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = isDarkMode ? '#e2e8f0' : '#333';
                  }}
                >
                  <i className={`fas ${isDarkMode ? 'fa-sun' : 'fa-moon'}`} style={{ fontSize: '16px' }}></i>
                  <span>{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>
                </button>
                <hr style={{ margin: '8px 0', borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#eee' }} />
                <div
                  className="profile-menu-build-info"
                  title={buildInfo.commitHash ? `Commit ${buildInfo.commitHash}` : ''}
                  style={{
                    padding: '6px 12px 8px',
                    fontSize: '11px',
                    lineHeight: 1.4,
                    color: isDarkMode ? 'rgba(255,255,255,0.55)' : '#8a8a8f',
                    userSelect: 'none',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span>Version</span>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                      v{buildInfo.version}
                      {buildInfo.commitHash ? ` · ${buildInfo.commitHash}` : ''}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 2 }}>
                    <span>Last update</span>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {formatBuildDate(buildInfo.commitDate || buildInfo.buildTime)}
                    </span>
                  </div>
                </div>
                <hr style={{ margin: '4px 0 8px', borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#eee' }} />
                <button
                  onClick={handleLogout}
                  className="profile-menu-item logout"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    width: '100%',
                    padding: '10px 12px',
                    border: 'none',
                    background: 'transparent',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontSize: '13px',
                    color: '#dc3545'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = isDarkMode ? 'rgba(220, 53, 69, 0.16)' : '#dc354510';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <i className="bi bi-box-arrow-right" style={{ fontSize: '16px' }}></i>
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal
        show={showChangePasswordModal}
        handleClose={() => setShowChangePasswordModal(false)}
      />

      {/* STOP button pulse + bell swing animations */}
      <style>{`
        @keyframes pulse-stop {
          0%, 100% { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.4); }
          50% { box-shadow: 0 0 0 8px rgba(220, 53, 69, 0); }
        }
        @keyframes bell-header-swing {
          0% { transform: rotate(-12deg); }
          100% { transform: rotate(12deg); }
        }
      `}</style>

    </header>
  );
};

export default Header;
